import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Plus, Download, Merge, Users, Search, Upload } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useDriverMaster } from '@/stores/driverMasterStore'
import { DataTable } from '@/components/common/DataTable'
import { CreateAliasDialog } from '@/components/driver-master/CreateAliasDialog'
import { DriverMergeDialog } from '@/components/driver-master/DriverMergeDialog'
import { ExportDialog } from '@/components/common/dialogs/ExportDialog'
import { ColumnVisibilityControl } from '@/components/ui/ColumnVisibilityControl'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { DriverMaster, CreateAliasData, DriverMergePreview } from '@/types/driverMaster'
import {
  getVisibleDriverColumns,
  DEFAULT_DRIVER_COLUMNS,
  type DriverActions
} from '@/components/reports/reportConfig'
import { PlatformBadge } from '@/components/ui/PlatformBadge'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { FeatureFlag } from '@/constants/featureFlags'
import { payoutCompanyService } from '@/services/payoutCompanyService'
import { PayoutCompany } from '@/types'

export function ForarePage() {
  const { currentUser } = useAuth()
  const navigate = useNavigate()
  const multiCompanyEnabled = useFeatureFlag(FeatureFlag.MULTI_COMPANY)
  const {
    filteredDrivers,
    loading,
    error,
    filters,
    loadDrivers,
    createAlias,
    generateMergePreview,
    mergeDrivers,
    setFilters,
    clearError,
    cleanup
  } = useDriverMaster()

  // Payout companies
  const [payoutCompanies, setPayoutCompanies] = useState<PayoutCompany[]>([])

  // Dialog states
  const [showExportDialog, setShowExportDialog] = useState(false)
  const [aliasDriver, setAliasDriver] = useState<DriverMaster | null>(null)

  // Merge states
  const [mergeMode, setMergeMode] = useState(false)
  const [mergePrimary, setMergePrimary] = useState<DriverMaster | null>(null)
  const [mergeSecondary, setMergeSecondary] = useState<DriverMaster | null>(null)
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false)
  const [mergePreview, setMergePreview] = useState<DriverMergePreview | null>(null)
  const [mergePreviewLoading, setMergePreviewLoading] = useState(false)

  // Expandable row state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  // Build default columns — include 'payoutCompany' when flag is on
  const defaultDriverColumns = useMemo(() => {
    if (!multiCompanyEnabled) return DEFAULT_DRIVER_COLUMNS
    const cols = [...DEFAULT_DRIVER_COLUMNS]
    // Insert before the 'actions' column (always last)
    cols.splice(cols.length - 1, 0, { key: 'payoutCompany', label: 'Bolag', visible: false })
    return cols
  }, [multiCompanyEnabled])

  // Column visibility
  const { columns: columnConfigs, visibleColumns, updateColumnVisibility, resetToDefaults } = useColumnVisibility({
    storageKey: 'driverColumns',
    defaultColumns: defaultDriverColumns
  })

  // Load drivers on mount
  useEffect(() => {
    if (currentUser) {
      loadDrivers(currentUser)
    }
  }, [currentUser, loadDrivers])

  // Load payout companies when flag is on
  useEffect(() => {
    if (!multiCompanyEnabled || !currentUser) return
    payoutCompanyService.getPayoutCompanies(currentUser.uid)
      .then(setPayoutCompanies)
      .catch(err => console.error('Failed to load payout companies:', err))
  }, [multiCompanyEnabled, currentUser])

  // Cleanup subscriptions on unmount
  useEffect(() => {
    return () => {
      cleanup()
      clearError()
    }
  }, [cleanup, clearError])


  // Handle navigate to create driver
  const handleNavigateToCreateDriver = () => {
    navigate('/driver/new')
  }

  // Handle create alias
  const handleCreateAlias = async (driverId: string, alias: CreateAliasData) => {
    if (!currentUser) return
    
    try {
      await createAlias(currentUser, driverId, alias)
      setAliasDriver(null)
    } catch (error) {
      // Error is handled in the store with toast
    }
  }

  // Handle initiate merge (from dropdown menu)
  const handleInitiateMerge = (primaryDriver: DriverMaster) => {
    setMergeMode(true)
    setMergePrimary(primaryDriver)
    setMergeSecondary(null)
  }

  // Handle secondary driver selection (clicking row in merge mode)
  const handleSecondaryDriverSelect = async (secondaryDriver: DriverMaster) => {
    if (!currentUser || !mergePrimary || !mergeMode) return

    setMergeSecondary(secondaryDriver)
    setMergePreview(null)
    setMergeDialogOpen(true)
    setMergePreviewLoading(true)

    try {
      const preview = await generateMergePreview(currentUser, mergePrimary.id, secondaryDriver.id)
      setMergePreview(preview)
    } catch (error) {
      console.error('Failed to generate merge preview:', error)
    } finally {
      setMergePreviewLoading(false)
    }
  }

  // Handle confirm merge
  const handleConfirmMerge = async (options: any) => {
    if (!currentUser || !mergePrimary || !mergeSecondary) return

    try {
      await mergeDrivers(currentUser, mergePrimary.id, mergeSecondary.id, options)
      setMergeDialogOpen(false)
      setMergeMode(false)
      setMergePrimary(null)
      setMergeSecondary(null)
      setMergePreview(null)
    } catch (error) {
      // Error is handled in the store with toast
    }
  }

  // Handle cancel merge mode
  const handleCancelMerge = () => {
    setMergeMode(false)
    setMergePrimary(null)
    setMergeSecondary(null)
    setMergePreview(null)
  }

  // Handle view details - navigate to driver page
  const handleViewDetails = (driver: DriverMaster) => {
    navigate(`/driver/${driver.id}`)
  }

  // Handle search
  const handleSearchChange = (value: string) => {
    setFilters({ search: value })
  }

  // Handle row expansion
  const handleRowExpand = (rowId: string | number) => {
    const id = String(rowId)
    setExpandedRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  // Create expandable content for showing aliases
  const createExpandableContent = (driver: DriverMaster) => {
    if (!driver.aliases || driver.aliases.length === 0) {
      return (
        <div className="text-muted-foreground text-sm">
          Inga alias registrerade för denna förare.
        </div>
      )
    }

    return (
      <div className="space-y-3">
        <div className="text-sm font-medium text-foreground mb-2">
          Alias ({driver.aliases.length})
        </div>
        <div className="grid gap-2">
          {driver.aliases.map((alias, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-background rounded-lg border border-border/50"
            >
              <div className="flex-1 grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Plattform:</span>
                  <div className="mt-1">
                    <PlatformBadge platform={alias.platform} />
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Namn:</span>
                  <div className="font-medium">{alias.name || '-'}</div>
                </div>
                <div className="min-w-0">
                  <span className="text-muted-foreground">UUID:</span>
                  <div className="font-mono text-xs truncate max-w-[200px]" title={alias.uuid || '-'}>
                    {alias.uuid || '-'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }


  // Row styling and click handling for merge mode
  const getRowClassName = (driver: DriverMaster) => {
    if (!mergeMode || !mergePrimary) return ''

    const isPrimary = driver.id === mergePrimary.id
    const isSecondarySelectable = driver.id !== mergePrimary.id

    if (isPrimary) {
      return 'bg-blue-50'
    } else if (isSecondarySelectable) {
      return 'hover:bg-blue-50 cursor-pointer'
    }

    return ''
  }

  const getRowClickHandler = (driver: DriverMaster) => {
    if (!mergeMode || !mergePrimary || driver.id === mergePrimary.id) {
      return undefined
    }

    return () => handleSecondaryDriverSelect(driver)
  }

  // Driver actions for the table
  const driverActions: DriverActions = {
    onCreateAlias: setAliasDriver,
    onViewDetails: handleViewDetails,
    onInitiateMerge: handleInitiateMerge,
    onSecondaryDriverSelect: handleSecondaryDriverSelect,
    mergeMode,
    mergePrimary,
    getRowClassName,
    getRowClickHandler,
    multiCompanyEnabled,
    payoutCompanies
  }

  // Create columns with actions
  const columns = useMemo(() => {
    const visibleColumnKeys = visibleColumns.map(col => col.key)
    return getVisibleDriverColumns(visibleColumnKeys, driverActions)
  }, [visibleColumns, driverActions])

  // Export functionality
  const handleExport = () => {
    setShowExportDialog(true)
  }

  // Merge mode UI component
  const mergeUI = mergeMode && mergePrimary ? (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Merge className="h-5 w-5 text-blue-600" />
          <div>
            <div className="font-medium text-blue-900">
              Slå samman förare
            </div>
            <div className="text-sm text-blue-700">
              Välj en förare att slå samman med <span className="font-medium">{mergePrimary.primaryName}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="flex items-center gap-1">
            Primär: {mergePrimary.primaryName}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancelMerge}
          >
            Avbryt
          </Button>
        </div>
      </div>
    </div>
  ) : null

  // Loading state
  if (loading && filteredDrivers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground animate-spin" />
          <p className="text-muted-foreground">Laddar förare...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={() => {
          clearError()
          if (currentUser) loadDrivers(currentUser)
        }}>Försök igen</Button>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <h1 className="text-3xl font-bold tracking-tight">Förare</h1>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => navigate('/forare/upload')}
              variant="outline"
              className="gap-2"
            >
              <Upload className="h-4 w-4" />
              Bulkuppladdning
            </Button>
            <Button onClick={handleNavigateToCreateDriver} className="gap-2">
              <Plus className="h-4 w-4" />
              Lägg till förare
            </Button>
            <Button onClick={handleExport} variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Exportera
            </Button>
          </div>
        </div>

        {/* Merge UI */}
        {mergeUI}

        <Separator />

        {/* Table Controls */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 justify-between">
          <div className="relative w-full sm:flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Sök förare, e-post, telefon, plattform..."
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ColumnVisibilityControl
              columns={columnConfigs}
              onVisibilityChange={updateColumnVisibility}
              onResetToDefaults={resetToDefaults}
            />
          </div>
        </div>

        {/* Table */}
        {filteredDrivers.length === 0 ? (
          <div className="text-center py-12 border rounded-lg">
            <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium mb-2">Inga förare registrerade</p>
            <p className="text-muted-foreground mb-4">
              Lägg till din första förare för att komma igång med förarhantering.
            </p>
            <Button onClick={handleNavigateToCreateDriver}>
              <Plus className="w-4 h-4 mr-2" />
              Lägg till förare
            </Button>
          </div>
        ) : (
          <DataTable
            data={filteredDrivers}
            columns={columns}
            expandableContent={createExpandableContent}
            expandedRows={expandedRows}
            onRowExpand={handleRowExpand}
            getRowId={(driver) => driver.id}
            getRowClassName={getRowClassName}
            onRowClick={(driver) => {
              const handler = getRowClickHandler(driver)
              if (handler) handler()
            }}
          />
        )}
      </div>

      {/* Dialogs */}
      {aliasDriver && (
        <CreateAliasDialog
          driver={aliasDriver}
          open={!!aliasDriver}
          onClose={() => setAliasDriver(null)}
          onSubmit={(alias) => handleCreateAlias(aliasDriver.id, alias)}
          loading={loading}
        />
      )}


      {mergePrimary && mergeSecondary && (
        <DriverMergeDialog
          primaryDriver={mergePrimary}
          secondaryDriver={mergeSecondary}
          preview={mergePreview}
          open={mergeDialogOpen}
          onClose={() => {
            setMergeDialogOpen(false)
            setMergePrimary(null)
            setMergeSecondary(null)
            setMergePreview(null)
          }}
          onConfirm={handleConfirmMerge}
          loading={loading}
          previewLoading={mergePreviewLoading}
        />
      )}

      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        data={filteredDrivers}
        columns={columns}
        baseFilename="drivers_report"
        title="Exportera förarlista"
        description="Välj format för export av förarlistan"
      />
    </>
  )
}
